import type { PitchFrame } from '@corocanto/scoring';
import processorUrl from './pitch-processor.ts?worker&url';
import { rawAudioContext } from './context';

/**
 * Open the microphone with the browser's voice processing switched off: echo cancellation,
 * noise suppression and automatic gain distort pitch and level. Wired headsets are assumed.
 */
export function openMicrophone(deviceId?: string): Promise<MediaStream> {
  return navigator.mediaDevices.getUserMedia({
    audio: {
      deviceId: deviceId ? { exact: deviceId } : undefined,
      echoCancellation: false,
      noiseSuppression: false,
      autoGainControl: false,
      channelCount: 1,
    },
    video: false,
  });
}

export function closeMicrophone(stream: MediaStream | null): void {
  stream?.getTracks().forEach((t) => t.stop());
}

export { listAudioDevices, type AudioDevice } from './devices';

/** The input latency the browser reports for a microphone stream, in seconds. */
export function inputLatencySec(stream: MediaStream): number {
  const settings = stream.getAudioTracks()[0]?.getSettings() as MediaTrackSettings & { latency?: number };
  return typeof settings?.latency === 'number' ? settings.latency : 0.01;
}

let workletReady: Promise<void> | null = null;

/**
 * Pitch tracking of a microphone stream in an AudioWorklet. Frames are timed on the shared
 * AudioContext clock, the same clock playback is scheduled on.
 */
export class PitchTracker {
  private readonly source: MediaStreamAudioSourceNode;
  private readonly node: AudioWorkletNode;
  private readonly sink: GainNode;
  private readonly listeners = new Set<(frames: PitchFrame[]) => void>();
  private collected: PitchFrame[] | null = null;

  private constructor(ctx: AudioContext, stream: MediaStream) {
    this.source = ctx.createMediaStreamSource(stream);
    this.node = new AudioWorkletNode(ctx, 'corocanto-pitch', {
      numberOfInputs: 1,
      numberOfOutputs: 1,
      outputChannelCount: [1],
      channelCount: 1,
      channelCountMode: 'explicit',
    });
    // A silent path to the output keeps the worklet running in every browser.
    this.sink = ctx.createGain();
    this.sink.gain.value = 0;
    this.source.connect(this.node);
    this.node.connect(this.sink).connect(ctx.destination);
    this.node.port.onmessage = (event: MessageEvent<PitchFrame[]>) => {
      const frames = event.data;
      if (this.collected) for (const f of frames) this.collected.push(f);
      for (const listener of this.listeners) listener(frames);
    };
  }

  static async create(stream: MediaStream): Promise<PitchTracker> {
    const ctx = rawAudioContext();
    if (!workletReady) {
      workletReady = ctx.audioWorklet.addModule(processorUrl);
      workletReady.catch(() => {
        workletReady = null;
      });
    }
    await workletReady;
    return new PitchTracker(ctx, stream);
  }

  onFrames(listener: (frames: PitchFrame[]) => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  /** Measure the power of one frequency on every frame (headset test tone), or 0 to stop. */
  setProbeFrequency(frequency: number): void {
    this.node.port.postMessage({ probeFrequency: frequency });
  }

  startCollecting(): void {
    this.collected = [];
  }

  stopCollecting(): PitchFrame[] {
    const frames = this.collected ?? [];
    this.collected = null;
    return frames;
  }

  dispose(): void {
    this.listeners.clear();
    this.node.port.onmessage = null;
    this.source.disconnect();
    this.node.disconnect();
    this.sink.disconnect();
  }
}
